import Phaser from "phaser";
import { EntityKind, type EntityState } from "odyssey-shared";
import type { NameplateChoices } from "../../settings.ts";
import type { WorldUiModule } from "../hook.ts";
import { inVisionCap, onScreen } from "../nameplates/layout.ts";
import { NameplatePool } from "../nameplates/pool.ts";
import type { ReducedMotion } from "../../reduced-motion.ts";
import { attitudeOf, attitudeOfPlate, barTone, creatureBarShown, npcFights } from "../creature-plates/style.ts";
import { HEALTH_BAR, HEALTH_BAR_TEXTURE, barFrame, barFrameOf, barLayout, barLockedOf, barPenaltyOf, barShown, forEachBarFrame, healthBarStrip } from "./bar.ts";
import { LOCKED_BAR_TEXTURE, forEachLockedFrame, lockedBarStrip, lockedFrame } from "./locked.ts";
import { CREATURE_BAR, CREATURE_BAR_TEXTURE, creatureBarFrameOf, creatureBarLayout, creatureHealthBarStrip, forEachCreatureBarFrame } from "./creature-bar.ts";
import type { BarReading, HealthBarReport } from "./report.ts";

/**
 * Health bars on the canvas (P2.1-T4; question 5: a bar under the nameplate of every character not at full health;
 * R-0089: a creature's once damaged or while it is the player's target, `creatureBarShown`; ADR-061 decisions 1 to 3
 * and 6), standing over the head each character's art reaches (`characters.ts`): one `Image` a character, its frame cut
 * from a strip drawn once into a canvas texture at mount (`bar.ts`; a creature's from its own strip in its attitude's
 * look, `creature-bar.ts`, R-0094), standing where the plate's name would and lifting the name over it. The UI camera
 * draws it, so a bar pixel is a HUD pixel at every world zoom.
 *
 * Bars come from a pool keyed by handle, as plates do (`nameplates/pool.ts`): taken when a character is drawn, kept
 * while it stays and returned when it leaves the client's view. A bar is hidden, and nothing about it is updated, while
 * the viewer's Nameplates choices draw no plate for its character (the player's own by default), while its character is
 * at full health or dead, concealed from the viewer and not targeted (ADR-061's amendment for P1.9-T73), outside the
 * vision cap around the local character (ADR-061 decision 6) or off the canvas. Its
 * frame changes only when the fill does, or below 10 percent when the flash's phase on the scene's clock does
 * (`barFrameOf`; under reduced motion a low bar stays lit in the steady colour, as the game's reduced motion setting
 * answers each frame), and it is placed only when its anchor or the UI scale moves, so a frame of steady play sets
 * nothing; the characters are visited through a function made once and the projection writes into a point made once.
 *
 * A character whose Buffs list carries a maximum-health penalty (ADR-079's amendment of 2026-10-03 for P2.2-T16) has its
 * fill drawn against the full maximum and a second image over the bar, the locked segment at its end (`locked.ts`),
 * placed with the bar and shown only while some width is locked; a creature's bar never locks.
 */

/**
 * One bar and what it shows.
 */
interface Bar {
  readonly image: Phaser.GameObjects.Image;
  readonly lock: Phaser.GameObjects.Image;
  texture: string;
  frame: string;
  locked: number;
  lockShown: boolean;
  anchorX: number;
  anchorY: number;
  scale: number;
  shown: boolean;
}

/**
 * Draws a strip into a canvas texture and cuts its frames, once a game.
 *
 * @param scene The scene.
 * @param key The texture's key.
 * @param strip The strip's size and pixels.
 * @param cell The size of each frame's cell.
 * @param frames Calls back with each frame's name and the index of its cell from the top.
 * @throws Error when the canvas texture cannot be made.
 */
function ensureStrip(scene: Phaser.Scene, key: string, strip: { readonly width: number; readonly height: number; readonly pixels: Uint8ClampedArray }, cell: { readonly width: number; readonly height: number }, frames: (each: (name: string, index: number) => void) => void): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const texture = scene.textures.createCanvas(key, strip.width, strip.height);
  if (texture === null) {
    throw new Error(`the ${key} strip's canvas texture could not be made`);
  }
  texture.context.putImageData(new ImageData(new Uint8ClampedArray(strip.pixels), strip.width, strip.height), 0, 0);
  frames((name, index) => {
    texture.add(name, 0, 0, index * cell.height, cell.width, cell.height);
  });
  texture.refresh();
}

/**
 * Hides a bar, if shown.
 *
 * @param bar The bar.
 */
function hide(bar: Bar): void {
  if (bar.shown) {
    bar.image.setVisible(false);
    bar.shown = false;
  }
  hideLock(bar);
}

/**
 * Hides a bar's locked segment, if shown.
 *
 * @param bar The bar.
 */
function hideLock(bar: Bar): void {
  if (bar.lockShown) {
    bar.lock.setVisible(false);
    bar.lockShown = false;
  }
}

/**
 * The health bars surface.
 *
 * @param report The game report's `healthBars` entry.
 * @param choices The viewer's Nameplates choices as they stand, read each frame (the nameplates' report).
 * @param motion The game's reduced motion, read each frame (`PAGE_MOTION`).
 * @returns The surface's module, listed after the nameplates.
 */
export function healthBars(report: HealthBarReport, choices: Readonly<NameplateChoices>, motion: ReducedMotion): WorldUiModule {
  return {
    id: "health-bars",
    /**
     * Draws the strip, builds the pool and the frame's visitor, and lists the bars for the report.
     *
     * @param context The UI layer, the projection and the characters the scene draws.
     * @returns The surface.
     */
    create(context) {
      const scene = context.scene;
      ensureStrip(scene, HEALTH_BAR_TEXTURE, healthBarStrip(), HEALTH_BAR, forEachBarFrame);
      ensureStrip(scene, CREATURE_BAR_TEXTURE, creatureHealthBarStrip(), CREATURE_BAR, forEachCreatureBarFrame);
      ensureStrip(scene, LOCKED_BAR_TEXTURE, lockedBarStrip(), HEALTH_BAR, forEachLockedFrame);
      const bars = new NameplatePool<Bar>(() => {
        const image = context.add(new Phaser.GameObjects.Image(scene, 0, 0, HEALTH_BAR_TEXTURE, barFrame(0)));
        image.setOrigin(0, 0).setVisible(false);
        const lock = context.add(new Phaser.GameObjects.Image(scene, 0, 0, LOCKED_BAR_TEXTURE, lockedFrame(0)));
        lock.setOrigin(0, 0).setVisible(false);
        return { image, lock, texture: HEALTH_BAR_TEXTURE, frame: barFrame(0), locked: 0, lockShown: false, anchorX: Number.NaN, anchorY: Number.NaN, scale: 0, shown: false };
      });
      const local = { x: 0, y: 0 };
      const anchor = { x: 0, y: 0 };
      const placed = { x: 0, y: 0 };
      const camera = context.worldCamera;
      let frame = 0;
      let now = 0;
      let reduced = false;
      let shown = 0;
      let ownHandle = -1;
      let targetHandle = -1;

      /**
       * Returns a bar its character has left, hidden and placed nowhere.
       *
       * @param bar The bar.
       */
      const release = (bar: Bar): void => {
        hide(bar);
        bar.anchorX = Number.NaN;
      };

      /**
       * Brings one drawn character's bar up to date for the frame.
       *
       * @param handle The character's handle.
       * @param x Its feet across, in world pixels.
       * @param y Its feet down, in world pixels.
       * @param state Its state, with its Health percentage and its kind: a creature's bar shows by the creature rule, as
       *   does an NPC's that fights; a non-combatant NPC's never shows.
       * @param head How far its art reaches above its feet, in world pixels.
       * @param hidden Whether its world UI hides (ADR-061's amendment for P1.9-T73): concealed from the viewer and not
       *   targeted; its bar is kept, hidden, so it shows the frame the character does.
       */
      const visit = (handle: number, x: number, y: number, state: EntityState, head: number, hidden: boolean): void => {
        const bar = bars.take(handle, frame);
        const npcLook = state.kind === EntityKind.Npc ? context.characters.npcLook(state.appearance) : null;
        const creature = state.kind === EntityKind.Creature || state.kind === EntityKind.Npc;
        const penalty = creature ? 0 : barPenaltyOf(state.buffs);
        const fights = state.kind !== EntityKind.Npc || (npcLook !== null && npcFights(npcLook.plate));
        const wanted = fights && (creature ? creatureBarShown(state.health, handle === targetHandle, choices) : barShown(state.health, handle === ownHandle, choices, penalty));
        if (hidden || !wanted || !inVisionCap(x - local.x, y - local.y)) {
          hide(bar);
          return;
        }
        const scale = context.scale.current;
        context.project(x, y - head, anchor);
        const anchorX = Math.round(anchor.x * scale);
        const anchorY = Math.round(anchor.y * scale);
        const width = HEALTH_BAR.width * scale;
        if (!onScreen(anchorX - width / 2, anchorY - 8 * scale, width, 8 * scale, camera.width, camera.height)) {
          hide(bar);
          return;
        }
        const look = state.kind === EntityKind.Creature ? context.characters.creatureLook(state.appearance) : null;
        const attitude = npcLook !== null ? attitudeOfPlate(npcLook.plate) : attitudeOf(look?.temperament ?? "aggressive");
        const texture = creature ? CREATURE_BAR_TEXTURE : HEALTH_BAR_TEXTURE;
        const name = creature ? creatureBarFrameOf(barTone(attitude), state.health, now, reduced) : barFrameOf(state.health, now, reduced, penalty);
        if (texture !== bar.texture) {
          bar.image.setTexture(texture, name);
          bar.texture = texture;
          bar.frame = name;
          bar.anchorX = Number.NaN;
          report.frames += 1;
        } else if (name !== bar.frame) {
          bar.image.setFrame(name);
          bar.frame = name;
          report.frames += 1;
        }
        if (anchorX !== bar.anchorX || anchorY !== bar.anchorY || scale !== bar.scale) {
          (creature ? creatureBarLayout : barLayout)(anchorX, anchorY, scale, placed);
          bar.image.setPosition(placed.x, placed.y);
          bar.lock.setPosition(placed.x, placed.y);
          bar.anchorX = anchorX;
          bar.anchorY = anchorY;
          bar.scale = scale;
          report.layouts += 1;
        }
        if (!bar.shown) {
          bar.image.setVisible(true);
          bar.shown = true;
        }
        const locked = barLockedOf(penalty);
        if (locked === 0) {
          hideLock(bar);
        } else {
          if (locked !== bar.locked) {
            bar.lock.setFrame(lockedFrame(locked));
            bar.locked = locked;
            report.frames += 1;
          }
          if (!bar.lockShown) {
            bar.lock.setVisible(true);
            bar.lockShown = true;
          }
        }
        shown += 1;
      };

      /**
       * Reads every bar held, for the report: made on request, never on a frame.
       *
       * @returns One reading a bar.
       */
      report.list = (): readonly BarReading[] => {
        const readings: BarReading[] = [];
        bars.forEachActive((bar, handle) => {
          readings.push({ handle, shown: bar.shown, frame: bar.image.frame.name, locked: bar.lockShown ? bar.locked : 0, x: bar.image.x, y: bar.image.y, inUiLayer: bar.image.displayList === context.layer });
        });
        return readings;
      };

      return {
        /**
         * Visits every character drawn this frame, then returns the bars of those that left.
         *
         * @param time The scene's clock in ms, which times a low bar's flash.
         */
        update(time) {
          frame += 1;
          now = time;
          reduced = motion.reduced();
          shown = 0;
          if (context.characters.local(local)) {
            ownHandle = context.characters.localHandle();
            targetHandle = context.characters.target();
            context.characters.forEach(visit);
          }
          bars.sweep(frame, release);
          report.active = bars.active;
          report.free = bars.free;
          report.created = bars.created;
          report.shown = shown;
        },
        /**
         * Forgets the report's list, which would hold the destroyed bars.
         */
        destroy() {
          report.list = () => [];
        },
      };
    },
  };
}
